import { createBrowserRouter, createRoutesFromElements, Navigate, redirect, Route } from "react-router-dom";
import MainLayout from "./MainLayout";
import { Login } from "./app/Login/Login";
import { getUserStore, useUserStore } from "./stores/userStore";
import { defaultLanding } from "./Navigation";
import { LoginError } from "./app/Login/LoginError";
import { BadLink } from "./app/Login/BadLink";
import { LinkSent } from "./app/Login/LinkSent";
import { verifyLogin } from "./app/Login/verifyLogin";
import { GlobalContext } from "./GlobalContext";
import { Content } from "./components/wrappers/Content";
import { Operators } from "./app/Operators/Operators";
import { AddOperator } from "./app/Operators/AddOperator";
import { Locations } from "./app/Locations/Locations";
import { Currencies } from "./app/Currencies/Currencies";
import { Payments } from "./app/Payments";
import { AddPayment } from "./app/Payments/AddPayment";
import { Yachts } from "./app/Yachts/Yachts";
import { YachtsTable } from "./app/Yachts/YachtsTable";
import { EditOperator, operatorLoader } from "./app/Operators/EditOperator";
import { Bookings } from "./app/Bookings/Bookings";
import { BookingsDashboard } from "./app/Bookings/BookingsDashboard";
import Home from "./app/Home/Home";
import Queue from "./app/Queue/Queue";
import CharterQueue from "./app/CharterQueue/CharterQueue";
import Fleet from "./app/Fleet/Fleet";
import { Offers } from "./app/Offers/Offers";
import { OffersTable } from "./app/Offers/OffersTable";
import { Enquiries } from "./app/Enquiries/Enquiries";
import { EnquiryDetails } from "./app/Enquiries/EnquiryDetails";
import { AddYacht } from "./app/Yachts/AddYacht";
import { EditYacht, yachtLoader } from "./app/Yachts/EditYacht";
import { AddOffer } from "./app/Offers/AddOffer";
import { EditOffer, dealLoader } from "./app/Offers/EditOffer";
import { OfferDetails } from "./app/Offers/OfferDetails";
import { Amenities } from "./app/Amenities/Amenities";
import { EditConfig } from "./app/Amenities/EditConfig";
import { TopLevelRegionsTable } from "./app/Locations/TopLevelRegionsTable";
import { BookingsTable } from "./app/Bookings/BookingsTable";
import { BookingDetails, bookingLoader } from "./app/Bookings/BookingDetails";
import { Options } from "./app/Options/Options";
import EditPaymentSchedule from "./app/Bookings/EditPaymentSchedule";
import { RegionsTable } from "./app/Locations/RegionsTable";
import { LocationsTable } from "./app/Locations/LocationsTable";
import { TownsTable } from "./app/Locations/TownsTable";
import { CountriesTable } from "./app/Locations/CountriesTable";
import { AddLocation } from "./app/Locations/forms/AddLocation";
import { AddCountry } from "./app/Locations/forms/AddCountry";
import { AddRegion } from "./app/Locations/forms/AddRegion";
import { AddTopLevelRegion } from "./app/Locations/forms/AddTopLevelRegion";
import { AddTown } from "./app/Locations/forms/AddTown";
import { countryLoader, EditCountry } from "./app/Locations/forms/EditCountry";
import { EditLocation, locationLoader } from "./app/Locations/forms/EditLocation";
import { EditRegion, regionLoader } from "./app/Locations/forms/EditRegion";
import { EditTopLevelRegion, topLevelRegionLoader } from "./app/Locations/forms/EditTopLevelRegion";
import { EditTown, townLoader } from "./app/Locations/forms/EditTown";
import { Customers } from "./app/Customers";
import { ApiYachts } from "./app/ApiYachts/ApiYachts";
import { PaymentsSummary } from "./app/PaymentsSummary";
import { Receivables } from "./app/Finance/OpenInvoicesScreen";
import { PayablesScreen as Payables } from "./app/Finance/PayablesScreen";
import CaList from "./app/Ca/CaList";
import CaBookingPage from "./app/Ca/CaBookingPage";
import CaYachtPage from "./app/Ca/CaYachtPage";
import PaymentsInbox from "./app/Finance/PaymentsInbox";

async function loginGuard() {
  // if the user is coming from hs to create a new offer save the redirect url for later
  const offerRedirectPathname = window.location.pathname;
  if (offerRedirectPathname.includes("/offers/add/hubspot/")) {
    localStorage.setItem("redirectUrl", offerRedirectPathname);
  }

  const user = getUserStore().user;
  if (!user) {
    return redirect("/login");
  }
  return null;
}

async function verifyLoginUrl({ request }: { request: Request }) {
  const url = new URL(request.url);
  const email = url.searchParams.get("email");
  const token = url.searchParams.get("token");
  if (!email || !token) {
    return redirect("/login/bad-link");
  }
  try {
    await verifyLogin(token, email);

    // if the user is coming from hs with hs deal url redirect them there
    const redirectUrl = localStorage.getItem("redirectUrl");
    if (redirectUrl) return redirect(redirectUrl);

    return redirect("/");
  } catch (e) {
    console.error(e);
    return redirect("/login/bad-link");
  }
}

async function loadUser() {
  const user = localStorage.getItem("user");
  if (user) {
    getUserStore().setUser(JSON.parse(user));
  }
  return null;
}

// Role-aware landing (design: broker → queue, finance → payments, viewer →
// bookings). Rendered at the index route so deep links and the rest of the
// nav are untouched — only "/" re-scopes per role.
function RoleLanding() {
  const user = useUserStore().user;
  const landing = user ? defaultLanding(user) : "/";
  if (landing !== "/") return <Navigate to={landing} replace />;
  return <Home />;
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<GlobalContext />} loader={loadUser}>
      <Route element={<MainLayout />} loader={loginGuard}>
        <Route index element={<RoleLanding />}></Route>
        <Route path="queue" element={<Queue />} />
        <Route path="charter-queue" element={<CharterQueue />} />
        <Route path="fleet" element={<Fleet />} />
        <Route path="options" element={<Options />} />
        <Route path="offers/add" element={<AddOffer />} />
        <Route path="offers/add/:id" element={<AddOffer />} />
        <Route path="offers/add/hubspot/:id" element={<AddOffer />} loader={dealLoader} />
        <Route path="offers/edit/:id" element={<EditOffer />} />
        <Route path="offers/edit" loader={() => redirect("/offers")} />
        <Route path="offers/:id" element={<OfferDetails />} />
        <Route path="offers" element={<Offers />}>
          <Route index element={<OffersTable />} />
        </Route>
        <Route path="enquiries/:id" element={<EnquiryDetails />} />
        <Route path="enquiries" element={<Enquiries />} />
        <Route path="operators">
          <Route index element={<Operators />} />
          <Route path="add" element={<AddOperator />} />
          <Route path=":id" element={<EditOperator />} loader={operatorLoader} />
        </Route>
        <Route path="customers">
          <Route index element={<Customers />} />
        </Route>
        <Route path="amenities" element={<Amenities />}>
          <Route index element={null} />
        </Route>
        <Route path="amenities/:id" element={<EditConfig />} />
        <Route path="locations" element={<Locations />}>
          <Route index element={<LocationsTable />} />
          <Route path="regions" element={<RegionsTable />} />
          <Route path="regions/add" element={<AddRegion />} />
          <Route path="regions/:regionId" element={<EditRegion />} loader={regionLoader as any} />

          <Route path="towns" element={<TownsTable />} />
          <Route path="towns/add" element={<AddTown />} />
          <Route path="towns/:townId" element={<EditTown />} loader={townLoader as any} />

          <Route path="countries" element={<CountriesTable />} />
          <Route path="countries/add" element={<AddCountry />} />
          <Route path="countries/:countryId" element={<EditCountry />} loader={countryLoader as any} />

          <Route path="top-level-regions" element={<TopLevelRegionsTable />} />
          <Route path="top-level-regions/add" element={<AddTopLevelRegion />} />
          <Route
            path="top-level-regions/:tlrId"
            element={<EditTopLevelRegion />}
            loader={topLevelRegionLoader as any}
          />

          <Route path="add" element={<AddLocation />} />
          <Route path=":locationId" element={<EditLocation />} loader={locationLoader as any} />
        </Route>
        <Route path="currencies" element={<Currencies />} />
        <Route path="payments">
          <Route index element={<Payments />} />
          <Route path="add" element={<AddPayment />} />
          <Route path="yachts" element={<Yachts />}>
            <Route index element={<YachtsTable />} />
          </Route>
        </Route>
        <Route path="payments-inbox" element={<PaymentsInbox />} />
        <Route path="receivables" element={<Receivables />} />
        <Route path="payables" element={<Payables />} />
        <Route path="ca" element={<CaList />} />
        <Route path="ca/yachts/:slug" element={<CaYachtPage />} />
        <Route path="ca/:id" element={<CaBookingPage />} />
        <Route path="payments-summary">
          <Route index element={<PaymentsSummary />} />
        </Route>
        <Route path="*" element={<Content title="Not found" />}></Route>
        <Route path="yachts/add" element={<AddYacht />} />
        <Route path="yachts/:id" element={<EditYacht />} loader={yachtLoader} />
        <Route path="yachts" element={<Yachts />}>
          <Route index element={<YachtsTable />} />
        </Route>

        <Route path="api-yachts" element={<ApiYachts />} />

        <Route path="bookings/:id" element={<BookingDetails />} loader={bookingLoader} />
        <Route path="bookings/:id/edit-payment-schedule" element={<EditPaymentSchedule />} />
        <Route path="bookings" element={<BookingsDashboard />} />
        <Route path="bookings/table" element={<Bookings />}>
          <Route index element={<BookingsTable />} />
        </Route>

        <Route path="*" element={<Content title="Not found" />} />
      </Route>

      <Route path="login">
        <Route index element={<Login />} />
        <Route path="link-sent" element={<LinkSent />} />
        <Route path="error" element={<LoginError />} />
        <Route path="bad-link" element={<BadLink />} />
        <Route path="verify" loader={verifyLoginUrl} />
      </Route>
    </Route>,
  ),
);
